import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { SALSAS, GRANEL_EXTRAS, PREPARACIONES, money } from "../data";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const Chip = ({ active, children, ...props }) => (
  <button
    type="button"
    className={`px-4 py-2 text-sm font-semibold border rounded-full transition-colors duration-200 ${
      active
        ? "bg-brand-burgundy text-brand-cream border-brand-burgundy"
        : "bg-white text-brand-burgundy border-brand-gold/50 hover:border-brand-gold"
    }`}
    {...props}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="mb-5">
    <p className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-2.5">
      {label}
    </p>
    {children}
  </div>
);

export const ProductModal = ({ product, onClose }) => {
  const { addItem, openCart } = useCart();
  const [qty, setQty] = useState(1);
  const [salsa, setSalsa] = useState([]);
  const [prep, setPrep] = useState(PREPARACIONES[0]);
  const [crema, setCrema] = useState("Con crema");
  const [queso, setQueso] = useState("Con queso");
  const [notes, setNotes] = useState("");
  const [extras, setExtras] = useState({});
  const choiceGroups = product.choiceGroups || [];
  const [choices, setChoices] = useState(() =>
    Object.fromEntries(choiceGroups.map((g) => [g.id, g.options[0]])),
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const isChilaquiles = product.type === "chilaquiles";
  const isGranel = product.type === "granel";
  const showSalsa = ["individual", "granel"].includes(product.type);

  const extrasList = useMemo(
    () =>
      GRANEL_EXTRAS.map((e) => ({ ...e, qty: extras[e.name] || 0 })).filter(
        (e) => e.qty > 0,
      ),
    [extras],
  );

  const extrasTotal = extrasList.reduce((s, e) => s + e.price * e.qty, 0);
  const unitPrice = product.price + extrasTotal;

  const setExtraQty = (name, delta) =>
    setExtras((prev) => {
      const next = Math.max(0, (prev[name] || 0) + delta);
      return { ...prev, [name]: next };
    });

  const handleAdd = () => {
    const item = {
      id: product.id,
      name: product.name,
      basePrice: product.price,
      type: product.type,
      qty,
      salsa: showSalsa ? salsa : null,
      prep: product.prep ? prep : null,
      crema: isChilaquiles ? crema : null,
      queso: isChilaquiles ? queso : null,
      selectedChoices: choiceGroups.map((g) => ({
        title: g.title,
        value: choices[g.id],
      })),
      notes: notes.trim(),
      extras: isGranel ? extrasList : [],
      unitPrice,
    };
    addItem(item);
    toast.success(`${product.name} agregado al pedido`);
    onClose();
    openCart();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="product-modal-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="product-modal"
          className="w-full sm:max-w-lg bg-brand-cream rounded-t-3xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        >
          <div className="sticky top-0 bg-brand-burgundy text-brand-cream px-6 py-5 flex items-start justify-between rounded-t-3xl sm:rounded-t-2xl z-10">
            <div>
              <h3 className="font-sans text-2xl font-extrabold">{product.name}</h3>
              <p className="text-brand-gold font-semibold mt-0.5">
                {money(product.price)}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              data-testid="modal-close-btn"
              className="text-brand-cream/80 hover:text-brand-cream p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="px-6 py-6">
            {showSalsa && (
              <p className="text-sm text-brand-burgundy/70 mb-6 bg-brand-gold/10 border border-brand-gold/30 px-4 py-3 rounded-lg">
                🌿 La verdura se entrega <strong>aparte</strong> por defecto. Si
                la quieres preparada en el platillo, indícalo en las notas.
              </p>
            )}

            {product.prep && (
              <Field label="Preparación">
                <div className="flex flex-wrap gap-2.5">
                  {PREPARACIONES.map((p) => (
                    <Chip
                      key={p}
                      active={prep === p}
                      onClick={() => setPrep(p)}
                      data-testid={`prep-${p}`}
                    >
                      {p}
                    </Chip>
                  ))}
                </div>
              </Field>
            )}

            {choiceGroups.map((g) => (
              <Field key={g.id} label={g.title}>
                <div className="flex flex-wrap gap-2.5">
                  {g.options.map((opt) => (
                    <Chip
                      key={opt}
                      active={choices[g.id] === opt}
                      onClick={() =>
                        setChoices((prev) => ({ ...prev, [g.id]: opt }))
                      }
                      data-testid={`choice-${g.id}-${opt}`}
                    >
                      {opt}
                    </Chip>
                  ))}
                </div>
              </Field>
            ))}

            {showSalsa && (
              <Field label="Elige tu salsa (puedes elegir varias)">
                <div className="flex flex-wrap gap-2.5">
                  {SALSAS.map((s) => (
                    <Chip
                      key={s}
                      active={salsa.includes(s)}
                      onClick={() =>
                        setSalsa((prev) =>
                          prev.includes(s)
                            ? prev.filter((x) => x !== s)
                            : [...prev, s],
                        )
                      }
                      data-testid={`salsa-${s}`}
                    >
                      {s}
                    </Chip>
                  ))}
                </div>
              </Field>
            )}

            {isChilaquiles && (
              <>
                <Field label="Crema">
                  <div className="flex flex-wrap gap-2.5">
                    {["Con crema", "Sin crema"].map((c) => (
                      <Chip key={c} active={crema === c} onClick={() => setCrema(c)}>
                        {c}
                      </Chip>
                    ))}
                  </div>
                </Field>
                <Field label="Queso">
                  <div className="flex flex-wrap gap-2.5">
                    {["Con queso", "Sin queso"].map((q) => (
                      <Chip key={q} active={queso === q} onClick={() => setQueso(q)}>
                        {q}
                      </Chip>
                    ))}
                  </div>
                </Field>
              </>
            )}

            {isGranel && (
              <Field label="Agregar extras (opcional)">
                <div className="space-y-2.5">
                  {GRANEL_EXTRAS.map((e) => (
                    <div
                      key={e.name}
                      className="flex items-center justify-between border border-brand-gold/30 rounded-lg px-4 py-2.5"
                    >
                      <div>
                        <span className="text-brand-burgundy font-medium text-sm">
                          {e.name}
                        </span>
                        <span className="text-brand-gold font-semibold text-sm ml-2">
                          +{money(e.price)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setExtraQty(e.name, -1)}
                          aria-label={`Disminuir ${e.name}`}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-brand-burgundy/30 text-brand-burgundy disabled:opacity-30"
                          disabled={!extras[e.name]}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-5 text-center font-semibold text-brand-burgundy">
                          {extras[e.name] || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => setExtraQty(e.name, 1)}
                          aria-label={`Aumentar ${e.name}`}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-burgundy text-brand-cream"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
            )}

            <Field label="Notas especiales (opcional)">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="product-notes"
                rows={2}
                placeholder="Ej. Quiero la verdura preparada en el platillo"
                className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy placeholder:text-brand-burgundy/40 focus:outline-none focus:border-brand-gold bg-white"
              />
            </Field>

            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  aria-label="Disminuir cantidad"
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-brand-burgundy/30 text-brand-burgundy"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span
                  data-testid="modal-qty"
                  className="w-8 text-center font-display text-xl font-bold text-brand-burgundy"
                >
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => setQty((q) => q + 1)}
                  aria-label="Aumentar cantidad"
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-burgundy text-brand-cream"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAdd}
              data-testid="modal-add-btn"
              className="w-full mt-6 bg-brand-gold text-brand-base font-bold uppercase tracking-widest text-sm py-4 rounded-xl hover:bg-brand-goldlight transition-colors"
            >
              Agregar · {money(unitPrice * qty)}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
